import {LegacyPeriodReportBoundary, useLegacyPeriodReportError, useLegacyPeriodReportState} from "../dashboard/OrderDashboard/Layout/LegacyPeriodReportBoundary.jsx";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import {useTranslation} from "react-i18next";
import React, {useContext, useEffect, useMemo, useState} from "react";
import DateRangePicker from "../../components/DateRangePicker.jsx";
import UpdateDropdown from "../../components/dashboard/UpdateDropdown.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import toast from "react-hot-toast";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import SeeMore from "../../components/SeeMore.jsx";
import LoadingCard from "../../components/LoadingCard.jsx";
import OverviewValue from "../../components/dashboard/OverviewValue.jsx";
import {GraphScale, GraphType} from "../../components/graphs/Graph.types.ts";
import DataGrid2 from "../../components/datagrid/DataGrid2.jsx";
import {useGetBackgroundColor,useGetBackgroundHoverColor,getPieColor} from "../../utils/graphColors.js";
import {useBrands} from "../../utils/brands/branding.js";
import BrandLogo from "../../utils/brands/BrandLogo.jsx";
import LineChart from "../../components/graphs/LineChart.jsx";
import PieChart from "../../components/graphs/PieChart.jsx";
import BarChart from "../../components/graphs/BarChart.jsx";
import ReportingInfoTooltip from "../../components/ReportingInfoTooltip.jsx";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import CampaignOrderBasis from "../dashboard/OrderDashboard/TablesAndGraphs/ByFilter/CampaignOrderBasis.jsx";
import {isSnapshotDateUnavailable, isSnapshotPeriodIncomplete, snapshotComparisonValue, snapshotHasPublishedDays, snapshotPeriodValue, withSnapshotMissingDays} from "../../utils/reportingSnapshotAvailability.js";
import {shippingMethodDisplayName} from "../../utils/shippingMethodPresentation.js";
import {legacyMarketingSalesSpendSlices, legacyMarketingSalesSummaryValues, nullableMoneyMajorUnitAmount} from "../../utils/reportingMetricRatios.js";
import {mediaSourceQualityBlocksMetrics} from "../../utils/reportingMetricPresentation.js";
import {selectProfitabilityMetric} from "../../utils/reportingPartialProfitability.js";
import {ProfitabilityMetricTooltip} from "../../components/reporting/ProfitabilityMetricTooltip.jsx";

export function legacyMarketingSalesProfitSelection({basic, partialProfitability, isMarketing, snapshotComplete, mediaSourceQuality = null}) {
    const suffix = isMarketing ? "Gross" : "Net";
    const standardValue = basic?.[`gain${suffix}`]?.value;
    if (mediaSourceQualityBlocksMetrics(mediaSourceQuality)) {
        return {value: null, partial: false, estimated: false, allowed: false};
    }
    if (!snapshotComplete) {
        return {value: null, partial: false, estimated: false, allowed: standardValue !== undefined || partialProfitability?.knownProfitAfterAllMarketing !== undefined};
    }

    const legacyPayload = partialProfitability === undefined;
    return selectProfitabilityMetric({
        fullValue: standardValue,
        fullComplete: basic?.[`purchaseCostComplete${suffix}Current`] === true
            || (!isMarketing && legacyPayload && standardValue !== undefined),
        partialProfitability,
        partialField: "knownProfitAfterAllMarketing",
    });
}

export function legacyMarketingSalesSummaryFromResponse(response) {
    return {
        ...(response?.basic ?? {}),
        partialProfitabilityMarketing: response?.partialProfitabilityMarketing,
    };
}

export function buildSnapshotAwareDateSeries(current, previous, currentSnapshot, previousSnapshot, toValue, mediaQualitySensitive = false) {
    const labels = withSnapshotMissingDays(currentSnapshot, Object.keys(current ?? {}));
    const previousDates = withSnapshotMissingDays(previousSnapshot, Object.keys(previous ?? {}));
    const currentValues = labels.map((date) => isSnapshotDateUnavailable(currentSnapshot, date)
        || (mediaQualitySensitive && mediaSourceQualityBlocksMetrics(currentSnapshot?.mediaSourceQuality))
        ? null
        : (current?.[date] === undefined ? null : toValue(current[date])));
    const previousValues = labels.map((_, index) => {
        const date = previousDates[index];
        return date === undefined || isSnapshotDateUnavailable(previousSnapshot, date)
            || (mediaQualitySensitive && mediaSourceQualityBlocksMetrics(previousSnapshot?.mediaSourceQuality))
            || previous?.[date] === undefined
            ? null
            : toValue(previous[date]);
    });

    return {labels, previousLabels: previousDates, currentValues, previousValues};
}

export function hasSnapshotDataForEitherPeriod(currentSnapshot, previousSnapshot) {
    return snapshotHasPublishedDays(currentSnapshot) || snapshotHasPublishedDays(previousSnapshot);
}

export function topChannelHeadlineValue(source, currentSnapshot) {
    return snapshotPeriodValue(source?.spendMarketing?.value ?? null, currentSnapshot);
}

export function comparisonTooltipTitle(isCurrent, currentDate, previousDate, previousPeriodLabel) {
    if (isCurrent) return currentDate;
    const match = String(previousDate ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const formattedDate = match ? `${Number(match[3])}.${Number(match[2])}.${match[1]}` : previousDate;
    return formattedDate ? `${previousPeriodLabel}\n${formattedDate}` : previousPeriodLabel;
}

export function buildComparisonKpiDatasets(series, {currentLabel, previousLabel, currency, type}) {
    return [series.currentValues, series.previousValues].map((values, seriesIndex) => {
        const isCurrent = seriesIndex === 0;
        return {
            label: isCurrent ? currentLabel : previousLabel,
            data: values.map((value, index) => ({
                key: series.labels[index],
                title: comparisonTooltipTitle(isCurrent, series.labels[index], series.previousLabels[index], previousLabel),
                value,
                currency,
                type,
            })),
            gray: !isCurrent,
            parsing: {xAxisKey: 'key', yAxisKey: 'value'},
        };
    });
}

function NewComponentsContent({project}) {
    const retention = useLegacyPeriodReportState();

    let {t} = useTranslation();
    let {et} = useLegacyPeriodReportError();

    let {
        since, until, centresAllowListed, domainsAllowListed,
    } = useContext(FilterContext);

    let authorityConfiguration = useAuthorityConfiguration();

    let renderCurrencyValue = useRenderCurrencyValue();
    let renderPercentValue = useRenderPercentValue();
    let renderNumber = useRenderNumber();

    let getBackgroundColor = useGetBackgroundColor();
    let getBackgroundHoverColor = useGetBackgroundHoverColor();

    const isMarketing = true;

    let [shopBasicStatisticsData, setShopBasicStatisticsData] = useState(null);
    let [turnoverByDomainData, setTurnoverByDomainData] = useState(null);
    let [statisticsBySourceData, setStatisticsBySourceData] = useState(null);
    let [snapshots, setSnapshots] = useState([]);
    let [countryShippingAndPaymentData, setCountryShippingAndPaymentData] = useState(null);
    let currentSnapshot = snapshots[0];
    let previousSnapshot = snapshots[1];
    let currentMediaSourceQuality = currentSnapshot?.mediaSourceQuality ?? shopBasicStatisticsData?.mediaSourceQuality;
    let rawSummaryValues = legacyMarketingSalesSummaryValues(shopBasicStatisticsData, isMarketing === true);
    let legacyProfitSelection = legacyMarketingSalesProfitSelection({
        basic: shopBasicStatisticsData,
        partialProfitability: shopBasicStatisticsData?.partialProfitabilityMarketing,
        isMarketing: isMarketing === true,
        snapshotComplete: currentSnapshot?.complete === true,
        mediaSourceQuality: currentMediaSourceQuality,
    });
    let summaryValues = !snapshotHasPublishedDays(currentSnapshot)
        ? Object.fromEntries(Object.keys(rawSummaryValues).map((key) => [key, null]))
        : {
            ...rawSummaryValues,
            gain: legacyProfitSelection.value ?? null,
            pno: mediaSourceQualityBlocksMetrics(currentMediaSourceQuality) ? null : rawSummaryValues.pno,
            gainChange: legacyProfitSelection.value === null || legacyProfitSelection.value === undefined || legacyProfitSelection.partial
                ? undefined
                : snapshotComparisonValue(rawSummaryValues.gainChange, currentSnapshot, previousSnapshot),
        };
    let sinceKey = since.format('YYYY-MM-DD');
    let untilKey = until.format('YYYY-MM-DD');
    let brands = useBrands();

    useEffect(() => {
        let active = true;

        setShopBasicStatisticsData(null);
        setTurnoverByDomainData(null);
        setStatisticsBySourceData(null);
        setSnapshots([]);
        new ErpCalculationApi(authorityConfiguration)
            .reportingLegacyMarketingAndSales({
                projectId: project.id,
                erpFilter: {
                    since: sinceKey,
                    until: untilKey,
                    selectedDomainIds: domainsAllowListed ?? [],
                    selectedCentreIds: centresAllowListed ?? [],

                }
            })
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setShopBasicStatisticsData(legacyMarketingSalesSummaryFromResponse(nextData));
                setTurnoverByDomainData(nextData.turnoverByDomain);
                setStatisticsBySourceData(nextData.statisticsBySource);
                setSnapshots([nextData.currentSnapshot, nextData.previousSnapshot]);
            })
            .catch((e) => {
                if (!active) {
                    return;
                }

                et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project.id, sinceKey, untilKey, centresAllowListed, domainsAllowListed]);

    useEffect(() => {
        let active = true;

        setCountryShippingAndPaymentData(null);
        new ErpCalculationApi(authorityConfiguration)
            .shopCountryPaymentAndShippingStatistics({
                projectId: project.id, dateRangeInput: {
                    since: sinceKey, until: untilKey,
                }
            })
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setCountryShippingAndPaymentData(nextData);
            })
            .catch((e) => {
                if (!active) {
                    return;
                }

                et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project.id, sinceKey, untilKey]);

    /**
     * @type {GraphData|null}
     */
    let turnoverByDomainInTimeBarChartData = useMemo(() => {
        if (turnoverByDomainData === null) {
            return null;
        }

        if (!snapshotHasPublishedDays(currentSnapshot)) {
            return {labels: [], type: GraphType.Bar, scale: GraphScale.Linear, datasets: []};
        }

        let firstDomain = turnoverByDomainData.dataByDomain[0];

        if (firstDomain === null) {
            return null;
        }

        let labels = withSnapshotMissingDays(currentSnapshot, Object.keys(firstDomain.turnoverNetGranular));

        return {
            labels: labels,
            type: GraphType.Bar,
            scale: GraphScale.Linear,
            datasets: Object.values(turnoverByDomainData.dataByDomain).map(domain => {
                let selectedData = isMarketing ? domain.turnoverGrossGranular : domain.turnoverNetGranular;
                let data = labels.flatMap((date) => {
                    if (isSnapshotDateUnavailable(currentSnapshot, date)) {
                        return [{key: date, title: date, value: null, currency: project.currency, type: 'money'}];
                    }
                    const value = selectedData?.[date];
                    return value?.amount > 0 ? [{key: date, title: date, value: value.amount / 100, currency: project.currency, type: 'money'}] : [];
                });

                return {
                    label: domain.projectDomain.hostName,
                    data: data,
                    parsing: {
                        xAxisKey: 'key',
                        yAxisKey: 'value'
                    }
                };
            })
                .filter((dataset) => {
                    const sum = Object.values(dataset.data).reduce((acc, value) => acc + value, 0);
                    return sum !== 0;
                })
        };
    }, [turnoverByDomainData, isMarketing, currentSnapshot]);

    /**
     * @type {{source: object, graph: GraphData}[]|null}
     */
    let topChannelGraphDataList = useMemo(() => {
        if (statisticsBySourceData === null) {
            return null;
        }

        if (!hasSnapshotDataForEitherPeriod(currentSnapshot, previousSnapshot)) return [];

        const sources = [
            statisticsBySourceData.topChannel1,
            statisticsBySourceData.topChannel2,
            statisticsBySourceData.topChannel3,
            statisticsBySourceData.otherChannels,
        ];

        return sources.map((source) => {
            if (!source) return null;

            const currentSpend = source.spendMarketing?.valueGranular ?? {};
            const previousSpend = source.spendMarketing?.valueGranularPreviousPeriod ?? {};
            const series = buildSnapshotAwareDateSeries(currentSpend, previousSpend, currentSnapshot, previousSnapshot, (value) => value?.amount == null ? null : value.amount / 100);
            const labels = series.labels;

            return {
                source: source,
                graph: {
                    labels: labels,
                    type: GraphType.Line,
                    scale: GraphScale.Linear,
                    showLegend: false,
                    showTooltip: true,
                    interaction: {
                        intersect: false,
                        mode: 'nearest',
                    },
                    maintainAspectRatio: false,
                    datasets: [series.currentValues, series.previousValues].map((values, index) => {
                        let isCurrent = index === 0;
                        const selectedDataValues = values.map((value, granularIndex) => ({
                            key: labels[granularIndex],
                            title: comparisonTooltipTitle(
                                isCurrent,
                                labels[granularIndex],
                                series.previousLabels[granularIndex],
                                t('reporting_lab.comparison.previous_period'),
                            ),
                            value,
                            currency: project.currency,
                            type: 'money',
                        }));

                        return {
                            label: isCurrent ? t('common.current-value') : t('common.previous-period-value'),
                            data: selectedDataValues,
                            gray: isCurrent === false,
                            parsing: {
                                xAxisKey: 'key',
                                yAxisKey: 'value'
                            }
                        };
                    })
                },
            };
        });
    }, [statisticsBySourceData, isMarketing, currentSnapshot, previousSnapshot]);

    let sourceCostQualityNeedsAttention = useMemo(() => {
        if (!statisticsBySourceData || !snapshotHasPublishedDays(currentSnapshot)) return false;
        const suffix = isMarketing ? 'Gross' : 'Net';
        const sources = [
            statisticsBySourceData.topChannel1,
            statisticsBySourceData.topChannel2,
            statisticsBySourceData.topChannel3,
            statisticsBySourceData.otherChannels,
        ].filter(Boolean);

        return sources.some((source) => source.purchaseCostQualityAvailable === false
            || source[`purchaseCostUnknown${suffix}`] > 0
            || source[`purchaseCostEstimated${suffix}`] > 0);
    }, [statisticsBySourceData, isMarketing, currentSnapshot]);

    /**
     * @type {GraphData|null}
     */
    let turnoverInTimeLineChartData = useMemo(() => {
        if (shopBasicStatisticsData === null) {
            return null;
        }

        let selectedData = isMarketing
            ? shopBasicStatisticsData.turnoverGross.valueGranular
            : shopBasicStatisticsData.turnoverNet.valueGranular;

        let selectedDataPreviousPeriod = isMarketing
            ? shopBasicStatisticsData.turnoverGross.valueGranularPreviousPeriod
            : shopBasicStatisticsData.turnoverNet.valueGranularPreviousPeriod;

        const series = buildSnapshotAwareDateSeries(selectedData, selectedDataPreviousPeriod, currentSnapshot, previousSnapshot, (value) => value?.amount == null ? null : value.amount / 100);
        let labels = series.labels;

        const datasets = buildComparisonKpiDatasets(series, {
            currentLabel: t('common.current-value'),
            previousLabel: t('common.previous-period-value'),
            currency: project.currency,
            type: 'money',
        });

        return {
            labels: labels,
            type: GraphType.Line,
            scale: GraphScale.Linear,
            showLegend: false,
            datasets: datasets,
        };
    }, [shopBasicStatisticsData, isMarketing, currentSnapshot, previousSnapshot]);

    /**
     * @type {GraphData|null}
     */
    let pnoKpiChartData = useMemo(() => {
        if (shopBasicStatisticsData === null) {
            return null;
        }

        let selectedData = isMarketing
            ? shopBasicStatisticsData.pnoGross.valueGranular
            : shopBasicStatisticsData.pnoNet.valueGranular;

        let selectedDataPreviousPeriod = isMarketing
            ? shopBasicStatisticsData.pnoGross.valueGranularPreviousPeriod
            : shopBasicStatisticsData.pnoNet.valueGranularPreviousPeriod;

        const series = buildSnapshotAwareDateSeries(selectedData, selectedDataPreviousPeriod, currentSnapshot, previousSnapshot, (value) => value ?? null, true);
        let labels = series.labels;

        const datasets = buildComparisonKpiDatasets(series, {
            currentLabel: t('common.current-value'),
            previousLabel: t('common.previous-period-value'),
            currency: null,
            type: 'percentage',
        });

        return {
            labels: labels,
            type: GraphType.Line,
            scale: GraphScale.Linear,
            showLegend: false,
            datasets: datasets,
        };
    }, [shopBasicStatisticsData, isMarketing, currentSnapshot, previousSnapshot]);

    /**
     * @type {GraphData|null}
     */
    let aovKpiChartData = useMemo(() => {
        if (shopBasicStatisticsData === null) {
            return null;
        }

        let selectedCalculation = isMarketing
            ? shopBasicStatisticsData.averageOrderValueGross
            : shopBasicStatisticsData.averageOrderValueNet;

        let selectedData = selectedCalculation?.valueGranular ?? {};
        let selectedDataPreviousPeriod = selectedCalculation?.valueGranularPreviousPeriod ?? {};

        const series = buildSnapshotAwareDateSeries(selectedData, selectedDataPreviousPeriod, currentSnapshot, previousSnapshot, nullableMoneyMajorUnitAmount);
        let labels = series.labels;

        const datasets = buildComparisonKpiDatasets(series, {
            currentLabel: t('common.current-value'),
            previousLabel: t('common.previous-period-value'),
            currency: project.currency,
            type: 'money',
        });

        return {
            labels: labels,
            type: GraphType.Line,
            scale: GraphScale.Linear,
            showLegend: false,
            datasets: datasets,
        };
    }, [shopBasicStatisticsData, isMarketing, currentSnapshot, previousSnapshot]);

    let spendOverviewSourceData = useMemo(() => {
        if (statisticsBySourceData === null) {
            return null;
        }

        if (!snapshotHasPublishedDays(currentSnapshot)) return [];

        return [
            ...Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories),
            ...Object.values(statisticsBySourceData.topOtherCategoriesChannels),
        ];
    }, [statisticsBySourceData, isMarketing, currentSnapshot]);

    let spendComparisonGraphData = useMemo(() => {
        if (!statisticsBySourceData) return null;
        if (!snapshotHasPublishedDays(currentSnapshot)) return {unavailable: true};

        const sources = [
            ...Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories),
            ...Object.values(statisticsBySourceData.topOtherCategoriesChannels),
        ];

        const slices = legacyMarketingSalesSpendSlices(sources);
        if (slices === null) {
            return {unavailable: true};
        }

        const labels = [];
        const dataSource = {
            data: [],
            backgroundColor: [],
            hoverBackgroundColor: [],
            borderWidth: 0,
        };

        slices.forEach(({source, index, amount}) => {
            labels.push(source.sourceName);
            dataSource.data.push(amount);
            dataSource.backgroundColor.push(getPieColor(index));
            dataSource.hoverBackgroundColor.push(getPieColor(index, 'hover'));
        });

        return {
            labels,
            type: GraphType.Pie,
            scale: GraphScale.Linear,
            showLegend: false,
            datasets: [dataSource],
        };
    }, [statisticsBySourceData, isMarketing, currentSnapshot]);

    return <WideSecureLayout periodReport={true} retentionExpired={retention?.retentionExpired}
        title={t('route.marketing-and-sales')}
        actions={<div className={'filter-btn flex justify-center gap-1 flex-wrap sm:justify-end'}>
            <UpdateDropdown/>
            <DateRangePicker/>
        </div>}
    >
        <ReportingSnapshotStatus snapshots={currentSnapshot} comparisonSnapshot={previousSnapshot} mediaSourceQuality={currentMediaSourceQuality}/>
        <div className="mt-3"><CampaignOrderBasis isMarketing={true}/></div>

        {sourceCostQualityNeedsAttention && <div role="status" className="app-surface mt-6 flex w-full items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
            <span className="font-medium">{t('reporting_lab.cost_quality.incomplete_short', {defaultValue: 'Neúplné náklady'})}</span>
            <ReportingInfoTooltip ariaLabel={t('reporting_lab.kpi.info_label', {defaultValue: 'Vysvětlení metriky'})}>
                {t('reporting_lab.cost_quality.legacy_source_notice', {defaultValue: 'U části objednávkových položek není nákupní cena dostupná nebo je pouze odhadovaná. Tento přehled proto zobrazuje obrat a marketing, nikoli úplnou produktovou ziskovost.'})}
            </ReportingInfoTooltip>
        </div>}

        <div className={'flex gap-6 w-full'}>
            <LoadingCard
                loading={shopBasicStatisticsData === null}
                title={t('project.base-statistical-data')}
                className={'mt-6 w-8/12 flex flex-col'}
                childrenClassName={'px-4 py-2 h-full flex flex-col grow-1'}
            >
                <div className={'flex flex-col justify-between grow-1'}>
                    {shopBasicStatisticsData && <div className="flex flex-wrap">
                        <OverviewValue
                            title={t('orders.turnover')}
                            titleSize={'2xl'}
                            value={renderCurrencyValue(summaryValues.turnover, true)}
                            percentChange={snapshotComparisonValue(isMarketing ? shopBasicStatisticsData.turnoverGross?.percentageChangeAgainstPreviousPeriod : shopBasicStatisticsData.turnoverNet?.percentageChangeAgainstPreviousPeriod, currentSnapshot, previousSnapshot)}
                        />
                        <OverviewValue
                            title={<span className="inline-flex items-center gap-1.5">{t('reporting_lab.table.contribution', {defaultValue: 'Hrubý zisk po reklamě'})}{legacyProfitSelection.partial && <ReportingInfoTooltip ariaLabel={t('reporting_lab.kpi.info_label', {defaultValue: 'Vysvětlení metriky'})}><ProfitabilityMetricTooltip metric="profit" partialProfitability={shopBasicStatisticsData?.partialProfitabilityMarketing}/><div className="mt-1">{t("reporting_lab.product_profitability.order_scope_help", {defaultValue: "Výsledek zahrnuje známé objednávkové řádky celého vybraného období a filtrů. Vyhledávání produktů ani stránkování tabulky tento souhrn nemění."})}</div></ReportingInfoTooltip>}</span>}
                            titleSize={'2xl'}
                            value={renderCurrencyValue(summaryValues.gain, true)}
                            percentChange={summaryValues.gainChange}
                        />
                        <OverviewValue
                            title={t('route.marketing')}
                            titleSize={'2xl'}
                            value={renderCurrencyValue(summaryValues.marketingSpend, true)}
                            percentChange={snapshotComparisonValue(shopBasicStatisticsData.spendMarketing?.percentageChangeAgainstPreviousPeriod, currentSnapshot, previousSnapshot)}
                        />
                        <OverviewValue
                            title={t('dashboard.orders-short')}
                            titleSize={'2xl'}
                            value={renderNumber(summaryValues.orders)}
                            percentChange={snapshotComparisonValue(isMarketing ? shopBasicStatisticsData.orderAmountGross?.percentageChangeAgainstPreviousPeriod : shopBasicStatisticsData.orderAmountNet?.percentageChangeAgainstPreviousPeriod, currentSnapshot, previousSnapshot)}
                            last={true}
                        />
                    </div>}
                    {turnoverInTimeLineChartData && <LineChart
                        data={turnoverInTimeLineChartData}
                        style={{height: '230px', maxHeight: '230px'}}
                    />}
                </div>
            </LoadingCard>

            <LoadingCard
                loading={shopBasicStatisticsData === null}
                title={t('project.kpi')}
                className={'mt-6 w-4/12'}
            >
                {shopBasicStatisticsData && <div className="flex flex-col flex-wrap">
                    <OverviewValue
                        title={t('dashboard.total-shop-pno')}
                        value={renderPercentValue(summaryValues.pno)}
                        last={true}
                    />
                    {pnoKpiChartData && <LineChart
                        data={pnoKpiChartData}
                        style={{'maxHeight': '100px'}}
                    />}
                    <OverviewValue
                        title={'AOV'}
                        value={renderCurrencyValue(summaryValues.averageOrderValue)}
                        last={true}
                    />
                    {aovKpiChartData && <LineChart
                        data={aovKpiChartData}
                        style={{'maxHeight': '100px'}}
                    />}
                </div>}
            </LoadingCard>
        </div>
        <div className={'mt-6 grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4'}>
            {[0, 1, 2, 3].map((index) =>
                <div key={'sourceGraph' + index} className="min-w-0">
                    <LoadingCard
                        loading={topChannelGraphDataList === null}
                        className={'w-full min-w-0'}
                    >
                        {topChannelGraphDataList && topChannelGraphDataList[index] && <>
                            <OverviewValue
                                title={topChannelGraphDataList[index].source.sourceName}
                                value={renderCurrencyValue(topChannelHeadlineValue(topChannelGraphDataList[index].source, currentSnapshot), true)}
                                last={true}
                            />
                            <LineChart
                                data={topChannelGraphDataList[index].graph}
                                style={{'maxHeight': '100px'}}
                            />
                        </>}
                    </LoadingCard>
                </div>
            )}
        </div>
        <div className={'flex gap-6 w-full col-turnover-in-time'}>
            <LoadingCard
                loading={turnoverByDomainData === null}
                title={t('project.turnover-in-time')}
                className={'mt-6 w-full h-[382px]'}
            >
                {turnoverByDomainInTimeBarChartData && <BarChart
                    data={turnoverByDomainInTimeBarChartData}
                    style={{height: '300px', maxHeight: '300px'}}
                />}
            </LoadingCard>
        </div>

        <div className={'flex gap-6 w-full mt-6'}>
            <LoadingCard
                loading={statisticsBySourceData === null}
                className={'w-8/12'}
                title={t('orders.top-channels')}
            >
                {statisticsBySourceData && snapshotHasPublishedDays(currentSnapshot) && <SeeMore heightClass={'h-60'}>
                    <DataGrid2
                        columns={[{
                            name: 'sourceName', header: t('dashboard.channel'), defaultFlex: 2,
                        }, {
                            name: 'orderAmount',
                            textAlign: 'end',
                            header: t('orders.orders'),
                            render: (row) => renderNumber(isMarketing ? row.data.orderAmountGross?.value : row.data.orderAmountNet?.value),
                        }, {
                            name: 'averageOrderValue', textAlign: 'end', header: 'AOV', render: (row) => {
                                return renderCurrencyValue(isMarketing ? row.data.averageOrderValueGross?.value : row.data.averageOrderValueNet?.value, true,);
                            },
                        }, {
                            name: 'spendMarketing',
                            textAlign: 'end',
                            header: t('dashboard.grid.spend-marketing'),
                            render: (row) => {
                                return renderCurrencyValue(row.data.spendMarketing?.value, true,);
                            },
                        }, {
                            name: 'turnover', textAlign: 'end', header: t('dashboard.turnover'), render: (row) => {
                                return renderCurrencyValue(isMarketing ? row.data.turnoverGross?.value : row.data.turnoverNet?.value, true,);
                            },
                        }]}
                        dataSource={Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories)}
                        hasTotal={false}
                    />
                </SeeMore>}

            </LoadingCard>


            <LoadingCard
                loading={statisticsBySourceData === null}
                className={'w-4/12'}
                title={t('orders.top-other-channels')}
            >
                {statisticsBySourceData && snapshotHasPublishedDays(currentSnapshot) && <DataGrid2
                    columns={[{
                        name: 'sourceName', header: t('dashboard.channel'), defaultFlex: 1,
                    }, {
                        name: 'amount',
                        textAlign: 'end',
                        header: t('dashboard.grid.spend-marketing'),
                        render: (row) => {
                            return renderCurrencyValue(row.data.spendMarketing?.value, true,);
                        },
                    }]}
                    dataSource={Object.values(statisticsBySourceData.topOtherCategoriesChannels)}
                />}
            </LoadingCard>
        </div>

        <div className={'flex gap-6 w-full mt-6'}>
            <LoadingCard
                loading={countryShippingAndPaymentData === null}
                className={'w-4/12'}
                title={t('orders.orders-by-country')}
            >
                {countryShippingAndPaymentData && <SeeMore heightClass={'h-[223px]'}>
                    <DataGrid2
                        columns={[{
                            name: 'countryName',
                            header: t('orders.country'),
                            defaultFlex: 1,
                            render: (row) => row.data.country.name,
                            percentage: {
                                width: 50,
                                color: getBackgroundColor(12),
                            },
                        }, {
                            name: 'amount', textAlign: 'end', header: t('orders.turnover'), render: (row) => {
                                return isMarketing ? renderCurrencyValue(row.data.turnoverGross, true) : renderCurrencyValue(row.data.turnoverNet, true)
                            },
                        }]}
                        dataSource={countryShippingAndPaymentData.countryStatistics}
                        truncateFirstColumn={false}
                    />
                </SeeMore>}
            </LoadingCard>
            <LoadingCard
                loading={countryShippingAndPaymentData === null}
                className={'w-4/12'}
                title={t('orders.orders-by-payment')}
            >
                {countryShippingAndPaymentData && <SeeMore heightClass={'h-[223px]'}>
                    <DataGrid2
                        columns={[{
                            name: 'paymentMethodName',
                            header: t('orders.payment-method'),
                            defaultFlex: 1,
                            render: (row) => row.data.paymentMethod.name,
                            percentage: {
                                width: 50,
                                color: getBackgroundColor(6),
                            },
                        }, {
                            name: 'amount', textAlign: 'end', header: t('orders.amount'), render: (row) => {
                                return isMarketing ? row.data.amountGross : row.data.amountNet
                            },
                        }]}
                        truncateFirstColumn={true}
                        dataSource={countryShippingAndPaymentData.paymentStatistics}
                    />
                </SeeMore>}
            </LoadingCard>
            <LoadingCard
                loading={countryShippingAndPaymentData === null}
                className={'w-4/12'}
                title={t('orders.orders-by-shipping')}
            >
                {countryShippingAndPaymentData && <SeeMore heightClass={'h-[223px]'}>
                    <DataGrid2
                        columns={[
                            {
                                name: 'shippingMethodText',
                                header: t('orders.shipping-method'),
                                defaultFlex: 1,
                                render: (row) => shippingMethodDisplayName(row.data.shippingMethod, t),
                                percentage: {
                                    width: 50,
                                    color: getBackgroundColor(0),
                                },
                            },
                            {
                                name: 'amount',
                                textAlign: 'end',
                                header: t('orders.amount'),
                                render: (row) => {
                                    return isMarketing ? row.data.amountGross : row.data.amountNet
                                },
                            }
                        ]
                        }
                        dataSource={countryShippingAndPaymentData.shippingStatistics}
                    />
                </SeeMore>}
            </LoadingCard>
        </div>


        <div className={'flex gap-6'}>
            <div className={'w-5/12 mt-6'}>
                <LoadingCard
                    loading={spendComparisonGraphData === null}
                    className={'w-full'}
                    title={t('orders.spend-comparison')}
                >
                    {spendComparisonGraphData?.unavailable
                        ? <div className="p-5 text-sm text-slate-500 dark:text-slate-400">{t('reporting_lab.marketing.spend_breakdown_unavailable', {defaultValue: 'Rozdělení nákladů není dostupné.'})}</div>
                        : project && spendComparisonGraphData && <PieChart
                            data={spendComparisonGraphData}
                            width={389}
                            height={250}
                            currency={project.currency}
                        />}
                </LoadingCard>
            </div>

            <div className={'w-7/12 mt-6'}>
                <LoadingCard
                    loading={spendOverviewSourceData === null}
                    className={'w-full'}
                    title={t('orders.spend-overview')}
                >
                    {spendOverviewSourceData && snapshotHasPublishedDays(currentSnapshot) && <SeeMore heightClass={'h-[264px]'}>
                        <DataGrid2
                            columns={[
                                {
                                    name: 'sourceName',
                                    header: t('dashboard.channel'),
                                    render: ({ data }) => {
                                        const sourceName = data.sourceName;
                                        const brandObj = brands[sourceName];  // This is the object from useBrands(), or undefined
                                        const displayName = brandObj?.name || sourceName;  // Use .name if object, else fallback to sourceName (string)
                                        return (
                                            <div className={'flex gap-2 items-center'}>
                                                <BrandLogo
                                                    brand={brandObj || sourceName}  // Pass object if exists, else string (adjust if BrandLogo requires object)
                                                    size={8}
                                                />
                                                {displayName}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    name: 'spendMarketing',
                                    textAlign: 'end',
                                    header: t('dashboard.grid.spend-marketing'),
                                    render: (row) => {
                                        return renderCurrencyValue(row.data.spendMarketing?.value, true,);
                                    },
                                },
                                {
                                    name: 'turnover',
                                    textAlign: 'end',
                                    header: t('dashboard.turnover'),
                                    render: (row) => {
                                        return renderCurrencyValue(isMarketing ? row.data.turnoverGross?.value : row.data.turnoverNet?.value, true,);
                                    },
                                }
                            ]}
                            dataSource={Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories)}
                            hasTotal={false}
                            truncateFirstColumn={false}
                        />
                    </SeeMore>}

                </LoadingCard>
            </div>
        </div>
    </WideSecureLayout>;
}

export default function NewComponents(props) {
    return <LegacyPeriodReportBoundary project={props.project}><NewComponentsContent {...props}/></LegacyPeriodReportBoundary>;
}
